import React from "react";
import { adminApi } from "../../api/admin";
import { changedNetworkNodes, networkPulsePath } from "../../utils/activityNetwork.mjs";

export default function useActivityNetwork() {
  const [network, setNetwork] = React.useState(null);
  const [status, setStatus] = React.useState("connecting");
  const [error, setError] = React.useState("");
  const [paused, setPaused] = React.useState(false);
  const [pulses, setPulses] = React.useState([]);
  const [pulseKey, nextPulse] = React.useReducer((value) => value + 1, 0);
  const [freshEvents, setFreshEvents] = React.useState([]);
  const [revision, refresh] = React.useReducer((value) => value + 1, 0);
  const snapshot = React.useRef(null);
  const sequence = React.useRef(0);

  React.useEffect(() => {
    const controller = new AbortController();
    const generation = ++sequence.current;
    let timer, busy = false;
    const load = async () => {
      if (busy || controller.signal.aborted || document.visibilityState === "hidden" || paused) return;
      busy = true;
      try {
        const response = await adminApi.activityNetwork({ signal: controller.signal });
        if (generation !== sequence.current || controller.signal.aborted) return;
        if (!response.network) throw new Error("No se recibió la red de actividad.");
        const next = response.network;
        const changed = changedNetworkNodes(snapshot.current, next);
        // Global automation without a person still illuminates the platform hub.
        const priorIds = new Set((snapshot.current?.events || []).map((event) => event.id));
        const addedEvents = snapshot.current ? (next.events || []).filter((event) => !priorIds.has(event.id)) : [];
        const globalChange = addedEvents.length > 0;
        snapshot.current = next; setNetwork(next); setStatus("live"); setError("");
        if (changed.length || globalChange) {
          setPulses(changed.length ? networkPulsePath(changed, next.nodes || []) : ["__webprofit__"]);
          setFreshEvents(addedEvents.slice(0, 8)); nextPulse();
        }
      } catch (err) {
        if (generation === sequence.current && !controller.signal.aborted) {
          setStatus("offline"); setError(err.message || "No se pudo actualizar la red.");
        }
      } finally {
        busy = false;
        if (!controller.signal.aborted) timer = window.setTimeout(load, snapshot.current?.refreshMs || 10000);
      }
    };
    const resume = () => {
      clearTimeout(timer);
      if (document.visibilityState === "hidden") { setStatus("hidden"); setPulses([]); }
      else load();
    };
    if (paused) { setStatus("paused"); setPulses([]); } else load();
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("online", resume);
    return () => {
      controller.abort(); clearTimeout(timer);
      document.removeEventListener("visibilitychange", resume); window.removeEventListener("online", resume);
    };
  }, [paused, revision]);
  // # GLOBAL NETWORK V2: pulse lifetime is independent of polling restarts;
  // refreshing the same snapshot cannot leave a stale glow on screen.
  React.useEffect(() => {
    if (!pulses.length) return undefined;
    const timer = window.setTimeout(() => setPulses([]), 3500);
    return () => window.clearTimeout(timer);
  }, [pulses]);
  return { network, status, error, paused, setPaused, pulses, pulseKey, freshEvents, refresh };
}
